/**
 * Responsible for rendering a list of underlying catalog items (products, materials, etc) which may be rendered in card or table view
 * Note that this may be a global product list, a list constrained within a season, a list of products within a grouped hierarchy ( Fabric -> Material -> Products for example), etc
 *
 * We have to deal with virtualization of products from the backend as well as resizing due to sidebar being shown/hidden, window size changes, zoom, etc.
 */
import {CatalogItemsCardViewStore, CatalogItemsCardViewStoreContext, LoadMoreProductsVirtualRow, ProductVirtualRow, useCatalogItemsCardViewStore, VirtualizedProductView, VirtualizedProductView_Masonry} from './'
import {runInAction} from 'mobx'
import {Fragment, useEffect} from 'react'
import {StackView, site, useCatalog, useGroupByGroupStore} from '@stores'
import {React, _, useAsObservableSource, classNames, pluralize} from '@api'
import {BuilderCardWithPopoverContainer, GroupByGroupStore, LoadingIndicator, NoProductsFound, ProductLandingComponent, useAssortmentCardStore, useVipLocalStore, utils} from '@design-board/components'
import {useLocalStore, observer} from 'mobx-react-lite'

import * as css from './CatalogItemsCardView.css'

export interface CatalogItemsPanelContentProps {
  index?: number
  queryKey?: string
  loading?: boolean | string
  onlineItemCount?: number
  offlineItemCount?: number
  path?: string[]
  itemStyle?: 'card' | 'flat'
  className?: string
  queryVariables?: any
  _source?: { _source: string[] }
  esIndex?: string
  visible?: boolean
  levelOfDetail?: number
  /**
   * When in a group our group may be expanded or not
   */
  expanded?: boolean
  preload?: boolean
  everVisible?: boolean
  onClickProduct?: (e: React.MouseEvent, id) => void
  level?: number // level of group by 0 for products
  groupStore?: GroupByGroupStore
  itemsPerClick?: number
  isStack?: boolean
  stackView?: StackView
}

export const CatalogItemsCardView_LoadMore = observer<CatalogItemsPanelContentProps>(function CatalogItemsCardView_LoadMore(props: CatalogItemsPanelContentProps) {
  const assortment = useAssortmentCardStore(), group = useGroupByGroupStore(),
        obsProps = useAsObservableSource({...props, assortment, group})
  const store = useVipLocalStore((ws) => new CatalogItemsCardViewStore(ws, obsProps))

  useEffect(() => store.registerEffects(), [])

  // useEffect(() => {
  //   console.log(`Mount CatalogItemsCardView_LoadMore`, props.groupStore?.fullTitle, {props})
  //   return () => console.log(`Unmount CatalogItemsCardView_LoadMore`, props.groupStore?.fullTitle, {props})
  // })

  const {showAfterEnoughTimeHasPassedWithoutSiblingsLoading, initialDataLoaded, groupNode,
      maxAllowedTimesClicked} = store,
    {level, className, visible, expanded, isStack, onlineItemCount, offlineItemCount} = props,
    {stores: {catalog, catalog: {settings: {showOnlyOfflineItems}}}} = store,
    count = onlineItemCount + offlineItemCount,
    // We may have higher timesClicked from earlier result sets with more answers, such as while typing and making a mistake and getting 0 results for a moment
    timesClicked = Number(Math.max(1, Number(Math.min(store.timesClicked, maxAllowedTimesClicked))))

  // Use internal loader instead
  // useEffect(() => {
  //   setTimeout(() => {
  //     catalog.isProductView && catalog.updateLoadingState({loading: !initialDataLoaded, loadingMessage: `${strings.loading} ${pluralize(catalog.catalogItemName)}`})
  //   }, 500)
  // }, [catalog.isProductView && !initialDataLoaded])

  // if (_.isNaN(timesClicked))
  useEffect(() => {
    if (store.timesClicked != timesClicked) {
      console.log(`Resetting times clicked from ${store.timesClicked} to ${timesClicked}`)
      store.timesClicked = Number(timesClicked)
    }
  }, [timesClicked > store.timesClicked])

  // Group node can get late initialized
  useEffect(() => {
    store.groupNode && (store.groupNode.productStore = store)
  }, [store.groupNode])

  // useEffect(() => {
  //   if (!_.isNumber(props.onlineItemCount)) {
  //     store.initialDataLoaded = true
  //   }
  // }, [props.onlineItemCount])

  // useEffect(() => {
  //   if (props.onlineItemCount && !catalog.loadingCounts) {
  //     store.initialDataLoaded = false
  //   }
  // }, [props.onlineItemCount, props.queryVariables, catalog.loadingCounts])

  // useFlipGroup({
  //                 flipId: 'catalog-items',
  //                 opts: {duration: 800},
  //                 deps: [props.count, store.sharedId, catalog.sortBy.order]
  //               })

  //console.log({loadingCounts: catalog.loadingCounts, catalogCount: catalog.onlineItemCount, onlineItemCount, visible, loadingIndex: store.loadingIndex, initialDataLoaded, timesClicked, count, offlineItemCount})

  // console.log({showLoader, isProductView})

  if (_.isNaN(timesClicked)) return <LoadingIndicator />

  return (
    <div className={classNames(className, css.root)}
         data-type='CatalogItemsCardView_LoadMore'
         style={{display: isStack && groupNode?.store?.parentStore && !groupNode.store.parentStore.allChildrenLoaded && !showAfterEnoughTimeHasPassedWithoutSiblingsLoading ? 'none' : 'inherit'}}>
      <CatalogItemsCardViewStoreContext.Provider value={store}>
        <div className={css.container}
          //ref={ref}
             data-level={level}
             data-visible={visible}
             data-expanded={expanded}
             data-loading={catalog.loading}
             data-item-count={count}
             data-lod={props.levelOfDetail}
             data-times-clicked={timesClicked}
             data-initial-load={!initialDataLoaded}
             data-offline-only={showOnlyOfflineItems}
             style={{'--item-count': count} as any}
        >
          <CatalogItemsCardViewContents />
        </div>
      </CatalogItemsCardViewStoreContext.Provider>
    </div>
  )
})

export const CatalogItemsCardViewContents = observer(function CatalogItemsCardViewContents() {
    const store = useCatalogItemsCardViewStore(),
    {
      itemsPerClick, onPageForward, onPageBack, hasMore, previousItemsPerClick, initialDataLoaded, onClickCard, offlineItems, showOfflineItemsOnly, groupNode,
      loadingIndicatorText, props, timesClicked,
    } = store,
    {isStack, stackView, onlineItemCount, offlineItemCount} = props,
    {stores: {currentUser: {userSettings: {dev}}, filter, catalog, catalog: {settings: {showOnlyOfflineItems, activeSearchText}, isProductView, groupBy, newlyCreatedIds}}} = store,
    count = onlineItemCount + offlineItemCount,
      virtualizedCatalog = dev?.virtualizedCatalog

  // useEffect(() => {
  //   console.log(`Mount CatalogItemsCardViewContents`)
  //   return () => console.log(`Unmount CatalogItemsCardViewContents`)
  // }, [])

  const emptyResult = filter?.hasFilterTerms
                      ? <NoProductsFound key='no-products-found'/>
                      : groupBy.isProductView ? <ProductLandingComponent catalog={catalog} key='product-landing' searchText={activeSearchText}/> : null,
    children = <Fragment>
      {initialDataLoaded && offlineItems.length ? <ProductVirtualRow items={offlineItems} offline onClickProduct={onClickCard} {...props} itemsPerClick={itemsPerClick}/> : null}
      {false && isProductView && newlyCreatedIds && <ProductVirtualRow items={offlineItems} offline onClickProduct={onClickCard} {...props} itemsPerClick={itemsPerClick}/>}
      {(!showOfflineItemsOnly || showOnlyOfflineItems) && _.range(timesClicked).map(i => <LoadMoreProductsVirtualRow key={i} index={i} {...props}/>)}
      {
        (showOfflineItemsOnly || showOnlyOfflineItems) && (
                                                         groupNode?.definition ? groupNode?.definition?.count > 0 ? (<HiddenCardsOfflineOnly count={groupNode.definition.count}/>)
                                                                                                                  : null
                                                                               : count > 0 && <HiddenCardsOfflineOnly count={onlineItemCount}/>)
      }
      {
        catalog.settings.showOnlyItemsWithChanges && !_.isEmpty(catalog.catalogItemsWithUnsavedChangesMap)
        ? <HiddenCardsOfflineOnly type={'modified'} count={(count - catalog?.catalogItemsWithUnsavedChanges?.length)}/>
        : null
      }
    </Fragment>

  return (<React.Fragment>
      {catalog.isProductView && !initialDataLoaded /*&& !store.everLoadedData */ && <LoadingIndicator className={css.fullWidthLoader} text={loadingIndicatorText}/>}

      {onlineItemCount == null
       ? <span data-id='null count'/>
       : onlineItemCount === 0 && offlineItems.length == 0 && initialDataLoaded
         ? emptyResult
         : (<div className={css.products}
                 data-virtualized={virtualizedCatalog}
                 data-display={'grid'}
                 data-type='ProductsFlexContainer'>
            {isStack ?
             <Fragment>
               {stackView != 'expanded' && timesClicked > 1 && <div className={css.previousCards} onClick={onPageBack}/>}
               {/* Show the existing page (if we have it) while loading so we don't lose the results onscreen while getting more results */}
               {store.isLoadingMore && (previousItemsPerClick != null && previousItemsPerClick != timesClicked) && <LoadMoreProductsVirtualRow index={previousItemsPerClick - 1} {...props} />}
               <LoadMoreProductsVirtualRow index={timesClicked - 1} {...props} />
               {stackView != 'expanded' && hasMore && <div className={css.nextCards} onClick={onPageForward}/>}
             </Fragment> :
             virtualizedCatalog && initialDataLoaded ?
             false ? <VirtualizedProductView_Masonry /> : <VirtualizedProductView />
             : children
             }
          </div>)
      }
    </React.Fragment>)
})

const HiddenCardsOfflineOnly = observer<{ count?: number, type?: string }>(function HiddenCardsOfflineOnly({count, type}) {
  const {toggleShowOnlyOfflineCatalogItems} = useCatalog()
  return <div className={css.warningCard} onClick={toggleShowOnlyOfflineCatalogItems}>{count} {pluralize('card', count)} are currently hidden - only showing {type ? type : 'offline'} items</div>
})

CatalogItemsCardView_LoadMore.displayName = 'CatalogItemsCardView_LoadMore'

CatalogItemsCardView_LoadMore.defaultProps = {
  level: 0,
  visible: true,
  everVisible: true
}
CatalogItemsCardView_LoadMore['whyDidYouRender'] = true



// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/GroupByCatalog_CatalogItems/CardView/LoadMoreProductCatalog/CatalogItemsCardView_LoadMore.tsx